import React, { useCallback, useRef } from "react";

// T22.187 (Kelly, 2026-10-13): Wheel Settings > Info > Description is a fixed
// 40px Froala box (DetailsTab passes editorHeight="40px" so Tags stays above
// the 1440x900 fold), so anything longer than one line scrolls inside a
// sliver and can't be read. The box needed to be drag-resizable.
//
// CSS `resize: vertical` on .fr-wrapper does NOT work here (live measured on
// web-stg main.5c953e00: injected resize:vertical, drag delta 0). Froala 4's
// size.refresh() rewrites .fr-wrapper's inline height from opts.height every
// time it runs, and FroalaTextEditor's ResizeObserver on that same wrapper
// calls size.refresh() on every resize -- so any native resize snaps straight
// back to 40px. The only height that survives is opts.height itself, so the
// grip writes there and lets Froala apply it.

export const RESIZE_KEY_STEP_PX = 20;
export const DEFAULT_MAX_EDITOR_HEIGHT_PX = 480;

export const clampEditorHeight = (px, minPx, maxPx) =>
  Math.round(Math.min(maxPx, Math.max(minPx, px)));

const currentEditorHeight = (editor) => {
  const wp = editor?.$wp?.get?.(0) || editor?.$wp?.[0];
  if (wp) {
    const h = wp.getBoundingClientRect().height;
    if (h) return h;
  }
  return parseFloat(editor?.opts?.height) || 0;
};

export const applyEditorHeight = (editor, px) => {
  if (!editor?.opts || !editor?.size?.refresh) return false;
  editor.opts.height = `${px}px`;
  editor.size.refresh();
  return true;
};

const EditorResizeGrip = ({
  editorRef,
  minPx,
  maxPx = DEFAULT_MAX_EDITOR_HEIGHT_PX,
  label = "Resize text box",
}) => {
  const dragRef = useRef(null);

  const resizeTo = useCallback(
    (px) => {
      const editor = editorRef?.current;
      if (!editor) return;
      applyEditorHeight(editor, clampEditorHeight(px, minPx, maxPx));
    },
    [editorRef, minPx, maxPx]
  );

  const onPointerDown = (e) => {
    const editor = editorRef?.current;
    if (!editor) return;
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startH: currentEditorHeight(editor) };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    resizeTo(drag.startH + (e.clientY - drag.startY));
  };

  const endDrag = (e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const onKeyDown = (e) => {
    const editor = editorRef?.current;
    if (!editor) return;
    const step = { ArrowDown: RESIZE_KEY_STEP_PX, ArrowUp: -RESIZE_KEY_STEP_PX }[e.key];
    if (!step) return;
    e.preventDefault();
    resizeTo(currentEditorHeight(editor) + step);
  };

  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-label={label}
      tabIndex={0}
      className="froalaResizeGrip"
      data-testid="froala-resize-grip"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
      title="Drag to resize"
      style={{
        height: 12,
        cursor: "ns-resize",
        touchAction: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 32,
          height: 4,
          borderRadius: 2,
          background: "#C4C7D0",
        }}
      />
    </div>
  );
};

export default EditorResizeGrip;
